import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, BookOpen, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { resolveImageUrl } from '@/lib/image';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [books, setBooks] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setSearched(true);

    try {
      const [booksRes, giftsRes] = await Promise.all([
        axios.get(`${API}/books?search=${searchQuery}`),
        axios.get(`${API}/gifts?search=${searchQuery}`),
      ]);
      setBooks(booksRes.data);
      setGifts(giftsRes.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-transition py-12 px-6 md:px-12">
      <div className="container mx-auto max-w-6xl">
        <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-primary mb-8" data-testid="search-page-title">
          Search Everything
        </h1>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-12">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search for books, gifts, authors, themes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" size="lg" className="rounded-full px-8" data-testid="search-button">
              Search
            </Button>
          </div>
        </form>

        {/* Results */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-muted-foreground" data-testid="search-loading">Searching...</p>
          </div>
        )}

        {!loading && searched && (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-8">
              <TabsTrigger value="all" data-testid="search-tab-all">
                All Results ({books.length + gifts.length})
              </TabsTrigger>
              <TabsTrigger value="books" data-testid="search-tab-books">
                Books ({books.length})
              </TabsTrigger>
              <TabsTrigger value="gifts" data-testid="search-tab-gifts">
                Gifts ({gifts.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              {books.length === 0 && gifts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground" data-testid="search-no-results">No results found for "{searchQuery}"</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {books.length > 0 && (
                    <div>
                      <h2 className="font-heading text-3xl font-light text-primary mb-6">Books</h2>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {books.map((book) => (
                          <Link key={book.id} to={`/books/${book.id}`} className="group">
                            <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                              <div className="h-48 bg-muted flex items-center justify-center">
                                {book.image_url ? (
                                  <img src={resolveImageUrl(book.image_url)} alt={book.title} className="w-full h-full object-cover" />
                                ) : (
                                  <BookOpen className="h-12 w-12 text-muted-foreground" />
                                )}
                              </div>
                              <div className="p-4">
                                <h3 className="font-heading text-xl group-hover:text-primary transition-colors">{book.title}</h3>
                                {book.author && <p className="text-sm text-muted-foreground">{book.author}</p>}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {gifts.length > 0 && (
                    <div>
                      <h2 className="font-heading text-3xl font-light text-primary mb-6">Gifts</h2>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {gifts.map((gift) => (
                          <Link key={gift.id} to={`/gifts/${gift.id}`} className="group">
                            <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                              <div className="h-48 bg-muted flex items-center justify-center">
                                {gift.image_url ? (
                                  <img src={resolveImageUrl(gift.image_url)} alt={gift.title} className="w-full h-full object-cover" />
                                ) : (
                                  <Gift className="h-12 w-12 text-muted-foreground" />
                                )}
                              </div>
                              <div className="p-4">
                                <h3 className="font-heading text-xl group-hover:text-primary transition-colors">{gift.title}</h3>
                                <p className="text-sm text-muted-foreground">{gift.category === 'new_mom' ? 'For New Moms' : 'For Kids'}</p>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="books">
              {books.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No books found</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {books.map((book) => (
                    <Link key={book.id} to={`/books/${book.id}`} className="group">
                      <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                        <div className="h-48 bg-muted flex items-center justify-center">
                          {book.image_url ? (
                            <img src={resolveImageUrl(book.image_url)} alt={book.title} className="w-full h-full object-cover" />
                          ) : (
                            <BookOpen className="h-12 w-12 text-muted-foreground" />
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading text-xl group-hover:text-primary transition-colors">{book.title}</h3>
                          {book.author && <p className="text-sm text-muted-foreground">{book.author}</p>}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="gifts">
              {gifts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No gifts found</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gifts.map((gift) => (
                    <Link key={gift.id} to={`/gifts/${gift.id}`} className="group">
                      <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                        <div className="h-48 bg-muted flex items-center justify-center">
                          {gift.image_url ? (
                            <img src={resolveImageUrl(gift.image_url)} alt={gift.title} className="w-full h-full object-cover" />
                          ) : (
                            <Gift className="h-12 w-12 text-muted-foreground" />
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading text-xl group-hover:text-primary transition-colors">{gift.title}</h3>
                          <p className="text-sm text-muted-foreground">{gift.category === 'new_mom' ? 'For New Moms' : 'For Kids'}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}